import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isNativeApp } from '../utils/platform';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [profileData, setProfileData] = useState({
    email: '',
    phone: '',
    user_type: '',
    email_verified: false,
    driver_profile: null,
    rider_profile: null
  });

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    email_notifications: true,
    sms_notifications: false,
    timezone: 'America/Chicago'
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/account-status`);
      setAccountStatus(response.data);
    } catch (err) {
      // Not a driver or endpoint unavailable - ignore
    }
  };

  const handleDeactivation = async () => {
    setDeactivating(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/drivers/request-deactivation`);
      setAccountStatus({ is_active: false, deactivated_at: new Date().toISOString() });
      setShowDeactivateConfirm(false);
      setMessage({ type: 'success', text: 'Your account has been deactivated. You will no longer appear to riders.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error deactivating account' });
    } finally {
      setDeactivating(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/me`);
      const data = response.data;
      setProfileData(data);

      // Get phone from the appropriate profile (driver_profile or rider_profile)
      let phone = '';
      if (data.driver_profile) {
        phone = data.driver_profile.phone || '';
      } else if (data.rider_profile) {
        phone = data.rider_profile.phone || '';
      }

      setFormData({
        email: data.email,
        phone: phone,
        email_notifications: data.driver_profile?.email_notifications ?? data.rider_profile?.email_notifications ?? true,
        sms_notifications: data.driver_profile?.sms_notifications ?? data.rider_profile?.sms_notifications ?? false,
        timezone: data.timezone || 'America/Chicago'
      });
    } catch (error) {
      if (error.response?.status !== 401) {
        setMessage({ type: 'error', text: 'Error loading profile' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Check if email field was modified but is actually the same (case-insensitive)
      const emailLooksChanged = formData.email !== profileData.email;
      const emailActuallySame = formData.email.toLowerCase() === profileData.email.toLowerCase();

      if (emailLooksChanged && emailActuallySame) {
        // User typed same email in different case - reset to original and continue
        setFormData(prev => ({ ...prev, email: profileData.email }));
      }

      // Check if email is being changed - requires verification flow
      if (formData.email.toLowerCase() !== profileData.email.toLowerCase()) {
        const emailResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/request-email-change`,
          { new_email: formData.email }
        ).catch(err => {
          if (err.response?.status !== 401) {
            setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to request email change' });
          }
          setSaving(false);
          throw err;
        });

        // Email change initiated - navigate to pending page
        // First save any other profile changes before navigating
        await saveOtherProfileChanges();

        // Navigate to email change pending page
        navigate(`/email-change-pending?email=${encodeURIComponent(formData.email)}`);
        return;
      }

      // No email change - just save other profile changes
      const changesMade = await saveOtherProfileChanges();

      if (changesMade) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Refresh profile data
        fetchProfile();
      } else {
        setMessage({ type: 'info', text: 'No changes to save.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating profile' });
    } finally {
      setSaving(false);
    }
  };

  const saveOtherProfileChanges = async () => {
    // Get current phone from profile
    let currentPhone = '';
    if (profileData.driver_profile) {
      currentPhone = profileData.driver_profile.phone || '';
    } else if (profileData.rider_profile) {
      currentPhone = profileData.rider_profile.phone || '';
    }

    // Get current notification preferences
    let currentEmailNotifications = true;
    let currentSmsNotifications = false;
    if (profileData.driver_profile) {
      currentEmailNotifications = profileData.driver_profile.email_notifications ?? true;
      currentSmsNotifications = profileData.driver_profile.sms_notifications ?? false;
    } else if (profileData.rider_profile) {
      currentEmailNotifications = profileData.rider_profile.email_notifications ?? true;
      currentSmsNotifications = profileData.rider_profile.sms_notifications ?? false;
    }

    // Check if timezone changed
    const currentTimezone = profileData.timezone || 'America/Chicago';

    // Update phone, notification preferences, and timezone via type-specific endpoint if changed
    if (formData.phone !== currentPhone ||
        formData.email_notifications !== currentEmailNotifications ||
        formData.sms_notifications !== currentSmsNotifications ||
        formData.timezone !== currentTimezone) {
      const profileEndpoint = profileData.user_type === 'driver'
        ? `${process.env.REACT_APP_API_URL}/api/profile/driver/profile`
        : `${process.env.REACT_APP_API_URL}/api/profile/rider/profile`;

      await axios.patch(profileEndpoint, {
        phone: formData.phone,
        email_notifications: formData.email_notifications,
        sms_notifications: formData.sms_notifications,
        timezone: formData.timezone
      });
      return true; // Changes were made
    }
    return false; // No changes were made
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/profile/me/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
    } catch (error) {
      if (error.response?.status !== 401) {
        setMessage({ type: 'error', text: error.response?.data?.detail || 'Error updating password' });
      }
    } finally {
      setSaving(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'info' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Account Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
          
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              {!profileData.email_verified && (
                <p className="text-sm text-amber-600 mt-1">
                  ⚠️ Email not verified. Check your inbox for verification link.
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                maxLength={14}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                US numbers only.
              </p>
            </div>

            {/* Notification Preferences */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Notification Preferences
              </label>
              <div className="space-y-3">
                {/* Email - Always enabled, cannot be changed */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-not-allowed"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    Email notifications
                  </span>
                  <span className="ml-2 text-xs text-gray-500">(Required)</span>
                </label>
                {/* SMS - Coming soon */}
                <label className="flex items-center opacity-50 cursor-not-allowed">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="w-4 h-4 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                  />
                  <span className="ml-3 text-sm text-gray-400">
                    SMS notifications
                  </span>
                  <span className="ml-2 text-xs text-amber-600 font-medium">(Coming soon)</span>
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Receive notifications for ride requests, updates, and important alerts via email.
              </p>
            </div>

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona (no DST)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Used for scheduling, availability, and notification times.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
        {/* Subscription Section - Drivers Only (hidden on native for App Store compliance) */}
        {profileData.user_type === 'driver' && profileData.subscription && !isNativeApp() && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription</h2>
            
            {profileData.subscription.tier ? (
              <div className="space-y-4">
                {/* Current Tier */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {profileData.subscription.tier.tier_name} Plan
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      profileData.subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : profileData.subscription.status === 'past_due'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {profileData.subscription.status ? 
                        profileData.subscription.status.charAt(0).toUpperCase() + 
                        profileData.subscription.status.slice(1).replace('_', ' ') 
                        : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-gray-600">Billing Cycle:</span>
                        <span className="ml-2 font-medium capitalize">
                        {profileData.subscription.billing_cycle || 'N/A'}
                        </span>
                    </div>
                    {profileData.subscription.billing_cycle === 'monthly' ? (
                        <div>
                        <span className="text-gray-600">Monthly Fee:</span>
                        <span className="ml-2 font-medium">
                            ${profileData.subscription.tier.monthly_fee?.toFixed(2) || '0.00'}
                        </span>
                        </div>
                    ) : profileData.subscription.billing_cycle === 'annual' ? (
                        <div>
                        <span className="text-gray-600">Annual Fee:</span>
                        <span className="ml-2 font-medium">
                            ${profileData.subscription.tier.annual_fee?.toFixed(2) || '0.00'}
                        </span>
                        </div>
                    ) : null}
                    <div>
                        <span className="text-gray-600">Platform Fee:</span>
                        <span className="ml-2 font-medium">
                        {profileData.subscription.tier.commission_rate}%
                        </span>
                    </div>
                    </div>
                  
                  {profileData.subscription.current_period_end && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <span className="text-sm text-gray-600">Next billing date: </span>
                      <span className="text-sm font-medium">
                        {new Date(profileData.subscription.current_period_end).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Manage Subscription Button */}
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/driver/subscription')}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Manage Subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">You don't have an active subscription.</p>
                <button
                  onClick={() => navigate('/driver/subscription')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Choose a Plan
                </button>
              </div>
            )}
          </div>
        )}
        {/* Password Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Password</h2>
          
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  minLength={8}
                />
                <p className="text-sm text-gray-500 mt-1">Must be at least 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    setMessage({ type: '', text: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Deactivate Account (Drivers Only) */}
        {profileData.user_type === 'driver' && accountStatus && (
          <div className="mt-6 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Status</h3>
            {!accountStatus.is_active ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium">Your account is deactivated.</p>
                <p className="text-red-700 text-sm mt-1">
                  You are hidden from riders and cannot receive new bookings. To request reactivation, email{' '}
                  <a href="mailto:support@ridehowl.com?subject=Reactivate Account" className="underline font-medium">
                    support@ridehowl.com
                  </a>{' '}
                  with Subject line "Reactivate Account".
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Deactivating your account will hide your profile from riders. You will not receive new ride
                  requests. You will retain full access to your dashboard, payout history, and tax documents.
                </p>
                {!showDeactivateConfirm ? (
                  <button
                    onClick={() => setShowDeactivateConfirm(true)}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Deactivate Account
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium mb-3">
                      Are you sure you want to deactivate your account?
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleDeactivation}
                        disabled={deactivating}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400 text-sm"
                      >
                        {deactivating ? 'Deactivating...' : 'Yes, Deactivate'}
                      </button>
                      <button
                        onClick={() => setShowDeactivateConfirm(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Account Type Badge */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Account Type: </span>
            <span className="capitalize">{profileData.user_type}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;